import React, { useState, useRef, useCallback, useEffect } from "react";
import Webcam from "react-webcam";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { useInterview } from "../../../services/InterviewProgressContext";

const FaceCalibration = ({ currentStep }) => {
  const webcamRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [currentdotStep, setCurrentdotStep] = useState(0);
  const [capturedImages, setCapturedImages] = useState({});
  const [showMessage, setShowMessage] = useState(
    "Please capture your profile photo."
  );
  const [countdown, setCountdown] = useState(null);
  const [calibrationFailed, setCalibrationFailed] = useState(false);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const candidateID = searchParams.get("candidate_id");
  const [captureProfileLoading, setCaptureProfileLoading] = useState(false);
  const [profileCaptured, setProfileCaptured] = useState(true);
  /**
   * Context for the step of the interview
   */
  const { goToStep } = useInterview();

  const positions = ["center", "top", "right", "bottom", "left"];

  const videoConstraints = {
    width: 300,
    height: 300,
    facingMode: "user",
  };

  const uploadImage = async (base64Image, position) => {
    try {
      const blob = await (await fetch(base64Image)).blob();
      const file = new File([blob], `face-${position.toLowerCase()}.png`, {
        type: "image/png",
      });

      const formData = new FormData();
      formData.append("image", file);

      await axios.post(
        `${process.env.REACT_APP_API_KEY}/calibrate/${position}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: {
            jd_id: id,
            candidate_id: candidateID,
            position,
          },
        }
      );
    } catch (error) {
      throw new Error("Upload failed");
    }
  };

  const uploadProfileImage = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    try {
      setCaptureProfileLoading(true);
      const blob = await (await fetch(imageSrc)).blob();
      const file = new File([blob], "face-profile.png", {
        type: "image/png",
      });

      const formData = new FormData();
      formData.append("image", file);

      await axios.post(
        `${process.env.REACT_APP_API_KEY}/capture_profile_photo`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: {
            jd_id: id,
            candidate_id: candidateID,
            position: "profile",
          },
        }
      );
      goToStep("faceCalibration");
      setShowMessage("Profile captured. Ready to start calibration.");
    } catch (error) {
      setShowMessage("Failed to upload profile. Please try again.");
    } finally {
      setCaptureProfileLoading(false);
    }
  };

  const onCalibrationComplete = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}/upload_calibration_data?jd_id=${id}&candidate_id=${candidateID}`
      );
      setShowMessage("All face positions captured!");
      setStarted(false);
      goToStep("startInterview");
    } catch (error) {
      setShowMessage("Calibration upload failed. Please restart calibration.");
      setCalibrationFailed(true);
      setStarted(false);
      setCountdown(null);
    }
  };

  const capture = useCallback(async () => {
    const position = positions[currentdotStep];
    const imageSrc = webcamRef.current.getScreenshot();

    try {
      setCapturedImages((prev) => ({
        ...prev,
        [position]: imageSrc,
      }));

      await uploadImage(imageSrc, position);

      if (currentdotStep === 0) {
        setShowMessage("Now follow the on-screen dots and arrows.");
      }

      if (currentdotStep < positions.length - 1) {
        setCurrentdotStep((prev) => prev + 1);
      } else {
        await onCalibrationComplete();
      }
    } catch (error) {
      setShowMessage("Calibration failed. Please restart calibration.");
      setCalibrationFailed(true);
      setStarted(false);
      setCountdown(null);
    }
  }, [currentdotStep]);

  const checkProfilePhoto = async () => {
    try {
      goToStep("faceCalibration", {
        profileExists: true,
      });
      const response = await axios.get(
        `${process.env.REACT_APP_API_KEY}/check_profile_photo`,
        {
          params: {
            jd_id: id,
            candidate_id: candidateID,
          },
        }
      );

      const data = response.data;

      if (
        data.status === "success" &&
        data.db_image_exists &&
        data.file_exists
      ) {
        setShowMessage(
          "Profile photo already exists. Ready to start calibration."
        );

        // ✅ Skip profilePicture step
        goToStep("faceCalibration", {
          profileExists: true,
        });
      } else {
        // ✅ Proceed to profilePicture step
        goToStep("profilePicture");
      }
    } catch (error) {
      console.error("Error checking profile photo:", error);
      // Optional: Fallback step or show error
      goToStep("profilePicture");
    }
  };

  useEffect(() => {
    checkProfilePhoto();
  }, []);

  useEffect(() => {
    if (!started || currentdotStep >= positions.length || calibrationFailed)
      return;

    setCountdown(5);
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(countdownInterval);
          setCountdown(null);
          capture();
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
    };
  }, [started, currentdotStep, capture, calibrationFailed]);

  const getDotPosition = (position) => {
    const arrowSize = "6px";
    const lineLength = "15%";
    switch (position.toLowerCase()) {
      case "top":
        return {
          dot: { top: "2%", left: "50%", transform: "translate(-50%, 0)" },
          arrow: {
            top: "calc(5% + 6px)",
            left: "49.7%",
            borderLeft: `${arrowSize} solid transparent`,
            borderRight: `${arrowSize} solid transparent`,
            borderBottom: `${arrowSize} solid red`,
          },
          line: {
            top: "calc(5% + 10px)",
            left: "50%",
            width: "2px",
            height: lineLength,
            backgroundColor: "red",
          },
        };
      case "bottom":
        return {
          dot: { bottom: "2%", left: "50%", transform: "translate(-50%, 0)" },
          arrow: {
            bottom: "calc(6px + 8px)",
            left: "49.7%",
            borderLeft: `${arrowSize} solid transparent`,
            borderRight: `${arrowSize} solid transparent`,
            borderTop: `${arrowSize} solid red`,
          },
          line: {
            bottom: "10%",
            left: "50%",
            width: "2px",
            height: lineLength,
            backgroundColor: "red",
          },
        };
      case "left":
        return {
          dot: { top: "50%", left: "2%", transform: "translate(0, -50%)" },
          arrow: {
            top: "49%",
            left: "calc(3% + 12px)",
            borderTop: `${arrowSize} solid transparent`,
            borderBottom: `${arrowSize} solid transparent`,
            borderRight: `${arrowSize} solid red`,
          },
          line: {
            top: "50%",
            left: "calc(4% + 2px)",
            width: lineLength,
            height: "2px",
            backgroundColor: "red",
          },
        };
      case "right":
        return {
          dot: { top: "50%", right: "2%", transform: "translate(0, -50%)" },
          arrow: {
            top: "49%",
            right: "calc(2% + 29px)",
            borderTop: `${arrowSize} solid transparent`,
            borderBottom: `${arrowSize} solid transparent`,
            borderLeft: `${arrowSize} solid red`,
          },
          line: {
            top: "50%",
            right: "calc(4% + 8px)",
            width: lineLength,
            height: "2px",
            backgroundColor: "red",
          },
        };
      case "center":
      default:
        return {
          dot: {
            top: "50%",
            right: "50%",
            zIndex: "100",
            width: "12px",
            height: "12px",
            backgroundColor: "red",
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
          },
        };
    }
  };

  const checkCalibration = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_KEY}/check_calibration_data?jd_id=${id}&candidate_id=${candidateID}`
      );

      if (response.data.status === "success") {
        return true;
      } else if (response.data.status === "error") {
        goToStep("startInterview");
        return false;
      }
    } catch (error) {
      console.error("Error:", error);
      goToStep("startInterview");
      return false;
    }
  };

  const handleRestart = () => {
    setStarted(true);
    setCurrentdotStep(0);
    setCapturedImages({});
    setCalibrationFailed(false);
    setShowMessage("Please capture your profile photo.");
  };

  const currentPosition = positions[currentdotStep];
  const dotData = getDotPosition(currentPosition);

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white relative px-4">
      {currentStep == "profilePicture" ? (
        <div className="flex flex-col items-center space-y-4 mb-8">
          <div className="rounded-full border-4 border-white overflow-hidden w-[300px] h-[300px]">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/png"
              videoConstraints={videoConstraints}
              className="w-full h-full object-cover"
            />
          </div>
          <button
            onClick={uploadProfileImage}
            className="mt-2 px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-semibold"
            disabled={captureProfileLoading}
          >
            {captureProfileLoading ? `Capture Profile` : `Capturing Profile...`}
          </button>
        </div>
      ) : (
        <>
          {started && currentdotStep < positions.length && dotData && (
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none z-10">
              <div
                className="absolute w-4 h-4 bg-red-500 rounded-full animate-pulse"
                style={dotData.dot}
              />
              {dotData.arrow && (
                <div
                  className="absolute"
                  style={{ width: "0", height: "0", ...dotData.arrow }}
                />
              )}
              {dotData.line && (
                <div className="absolute bg-red-500" style={dotData.line} />
              )}
            </div>
          )}

          {!started && (
            <h2 className="text-2xl font-bold mb-4">{showMessage}</h2>
          )}

          <div className="flex flex-col lg:flex-row gap-8 items-center justify-center">
            {/* Webcam Section */}
            {started && currentdotStep < positions.length && (
              <div className="rounded-full border-4 border-white overflow-hidden w-[300px] h-[300px]">
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/png"
                  videoConstraints={videoConstraints}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Instructions Panel */}
            {!started && (
              <div className="max-w-md text-white p-6 rounded-lg bg-gray-800 bg-opacity-30">
                <h2 className="text-xl mb-4 font-semibold">
                  Calibration Instructions
                </h2>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li>Position your face within the camera frame.</li>
                  <li>
                    A red dot will appear in five directions (left, right, top,
                    bottom, center). Please look at each red dot continuously
                    for 5 seconds to complete calibration.
                  </li>
                  <li>
                    Make sure to follow the red dot carefully in each direction;
                    failure to do so will result in unsuccessful calibration.
                  </li>
                  <li>
                    Calibration will begin once you click the button below.
                  </li>
                </ul>

                {!started &&
                  currentdotStep < positions.length &&
                  !calibrationFailed && (
                    <button
                      onClick={async () => {
                        const shouldStart = await checkCalibration();
                        if (shouldStart) {
                          setStarted(true);
                        } else {
                          setShowMessage("Calibration already completed.");
                        }
                      }}
                      className="mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold w-full"
                    >
                      {currentdotStep === 0 ? "Start Calibration" : "Continue"}
                    </button>
                  )}
              </div>
            )}
          </div>

          {countdown !== null && (
            <div className="text-6xl font-bold mt-4 text-yellow-400">
              {countdown}
            </div>
          )}

          {calibrationFailed && (
            <button
              onClick={handleRestart}
              className="mt-6 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold"
            >
              Restart Calibration
            </button>
          )}

          {currentdotStep === positions.length && (
            <p className="mt-6 text-green-400 font-semibold">
              All face positions captured and uploaded.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default FaceCalibration;
